// src/components/elevation/ElevationBackfillPanel.js
// Admin: coverage of ground elevation across the register + run / cancel the TessaDEM backfill (live via websocket).
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, HStack, VStack, Text, Button, Badge, Progress, Checkbox, CheckboxGroup, Wrap, WrapItem, Switch,
  FormControl, FormLabel, useToast, useColorModeValue, Skeleton,
} from '@chakra-ui/react';
import webSocketService from '../../services/websocketService';
import { fetchElevationStatus, startElevationBackfill, cancelElevationBackfill } from '../../services/elevationService';

const SCOPES = [
  ['assets', 'Assets (points, roads, wells)'],
  // --- UPDATE (roads v1.3): mapped roads without segments are sliced first, then every segment gets start / mid / end
  ['road_network', 'Slice unsegmented mapped roads'], ['road_segments', 'Road segments (start · mid · end)'],
  // --- END UPDATE
  ['pipelines', 'Pipelines'], ['sensors', 'Sensors'], ['road_sub_assets', 'Road sub-assets'],
];

const ElevationBackfillPanel = () => {
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [scope, setScope] = useState(SCOPES.map(([k]) => k));
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const card = useColorModeValue('white', 'gray.800');

  const load = useCallback(async () => {
    try { setStatus(await fetchElevationStatus()); }
    catch (e) { toast({ status: 'error', title: 'Elevation status unavailable', description: e?.response?.data?.message || e.message }); }
  }, [toast]);

  useEffect(() => {
    load();
    const onProgress = (msg) => {
      const job = msg?.payload ?? msg?.data ?? msg; // websocket frames arrive as {type, payload}
      if (!job || typeof job !== 'object') return;
      setStatus((s) => ({ ...(s || {}), job }));
      if (job && !job.running) load();            // finished → refresh coverage
    };
    const off = webSocketService.subscribe('elevation_backfill_progress', onProgress);
    return () => { if (typeof off === 'function') off(); else webSocketService.unsubscribe('elevation_backfill_progress', onProgress); };
  }, [load]);

  const run = async () => {
    setBusy(true);
    try { await startElevationBackfill({ scope, force }); toast({ status: 'info', title: 'Elevation backfill started' }); await load(); }
    catch (e) { toast({ status: 'warning', title: 'Backfill not started', description: e?.response?.data?.message || e.message }); }
    finally { setBusy(false); }
  };
  const cancel = async () => { try { await cancelElevationBackfill(); } finally { load(); } };

  const job = status?.job;
  const cov = status?.coverage || {};
  const api = job?.api;
  return (
    <Box p={5} bg={card} borderRadius="xl" borderWidth="1px">
      <HStack justify="space-between" mb={3}>
        <VStack align="start" spacing={0}>
          <Text fontWeight="800">Ground elevation · TessaDEM</Text>
          <Text fontSize="xs" color="gray.500">Terrain height from each asset&apos;s GPS — feeds the flood model and road ponding analysis.</Text>
        </VStack>
        <Badge colorScheme={api?.configured ? 'green' : 'red'}>{api?.configured ? 'API key set' : 'API key missing'}</Badge>
      </HStack>

      {!status ? <Skeleton h="120px" /> : (
        <VStack align="stretch" spacing={3}>
          {Object.entries(cov).filter(([, v]) => v && typeof v === 'object').map(([k, v]) => {
            const pct = v.total ? Math.round((v.with_elevation / v.total) * 100) : 0;
            return (
              <Box key={k}>
                <HStack justify="space-between" fontSize="xs">
                  <Text fontWeight="600" textTransform="capitalize">{k.replace(/_/g, ' ')}</Text>
                  <Text color="gray.500">
                    {v.with_elevation} / {v.total}{v.no_gps ? ` · ${v.no_gps} without GPS` : ''}
                    {/* --- UPDATE (roads v1.3) */}
                    {v.with_midpoint != null ? ` · ${v.with_midpoint} with mid-point` : ''}
                    {k === 'roads' && v.mapped != null ? ` · ${v.mapped} mapped · ${v.segmented} sliced` : ''}
                    {/* --- END UPDATE */}
                  </Text>
                </HStack>
                <Progress value={pct} size="xs" colorScheme="teal" borderRadius="full" />
              </Box>
            );
          })}
          <Text fontSize="2xs" color="gray.500">
            Cached points: {cov.cache_points ?? 0}
            {api ? ` · API requests this run-time: ${api.requests}${api.last_balance != null ? ` · balance ${api.last_balance}` : ''}` : ''}
          </Text>

          <CheckboxGroup value={scope} onChange={setScope} isDisabled={job?.running}>
            <Wrap spacing={3}>{SCOPES.map(([k, l]) => <WrapItem key={k}><Checkbox size="sm" value={k}>{l}</Checkbox></WrapItem>)}</Wrap>
          </CheckboxGroup>
          <FormControl display="flex" alignItems="center">
            <Switch size="sm" isChecked={force} onChange={(e) => setForce(e.target.checked)} isDisabled={job?.running} mr={2} />
            <FormLabel fontSize="xs" mb={0}>Re-fetch assets that already have elevation (cached points are still free)</FormLabel>
          </FormControl>

          {job?.running && (
            <Box>
              <Text fontSize="xs" mb={1}>Running: <b>{job.step?.replace(/_/g, ' ')}</b> · done {Object.entries(job.done || {}).map(([k, n]) => `${k} ${n}`).join(' · ') || '0'}</Text>
              <Progress size="xs" isIndeterminate colorScheme="teal" />
            </Box>
          )}
          {job?.error && <Text fontSize="xs" color="red.400">Last run stopped: {job.error}</Text>}
          {!job?.running && job?.finished_at && !job?.error && <Text fontSize="xs" color="green.500">Last run finished {new Date(job.finished_at).toLocaleString()}</Text>}

          <HStack>
            <Button size="sm" colorScheme="teal" onClick={run} isLoading={busy} isDisabled={job?.running || !api?.configured || !scope.length}>Run backfill</Button>
            {job?.running && <Button size="sm" variant="outline" onClick={cancel}>Cancel</Button>}
          </HStack>
        </VStack>
      )}
    </Box>
  );
};

export default ElevationBackfillPanel;
